import { createAnimationLegacy } from '../common/schemas/legacy/animation';
import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import type { LegacyVideo, LegacyVideoDefault, LegacyVideoZero, VideoEntity } from './schemas';

/**
 * Which legacy placement the factory should produce — corresponds to the
 * envelope's `type` discriminator.
 *  - `'default'` (or omitted): a video dropped into a normal column. The
 *    persisted envelope omits the `type` field entirely for byte-identical
 *    parity with pre-discriminator data on disk. Data uses the simplified
 *    `horizontalPosition` + `maxWidth` layout shorthand.
 *  - `'zero'`: a video dropped into a zero block. Envelope carries `type: 'zero'`;
 *    data uses the extended-position grid plus hide/animation/isSynchronizeStyle.
 */
export type LegacyVideoVariant = 'default' | 'zero';

/**
 * Product defaults for the play-button overlay: large size, white button on a
 * dark icon at rest, brand-blue button with a white icon on hover. Shared by
 * both placement variants — a fresh factory helper so the two `create*Data`
 * builders never accidentally share (and mutate) one object reference.
 */
const createPlayButtonDefaults = (): LegacyVideoDefault['data']['playButton'] => ({
    size: 'L',
    backgroundColor: '#fff',
    iconColor: '#333',
    hoverBackgroundColor: '#339ecc',
    hoverIconColor: '#fff',
});

const createDefaultData = (): LegacyVideoDefault['data'] => ({
    videoType: 'youtube',
    videoUrl: '',
    videoFile: null,
    previewUrl: '',
    customPreviewFile: null,
    customPreviewUrl: '',
    customPreviewSize: 'contain',
    placeholder: 'https://youtube.com',
    isShorts: false,
    playButton: createPlayButtonDefaults(),
    horizontalPosition: 'M',
    maxWidth: null,
});

/**
 * Default zero-block dimensions for video — wider/taller than text or button
 * because the player needs a 16:9 area. Matches the host-app form factory
 * defaults that the FormGroup-based zero widgets pass into
 * `webWidgetExtendedPositionSettingsFormGroup`.
 */
const createZeroData = (): LegacyVideoZero['data'] => ({
    videoType: 'youtube',
    videoUrl: '',
    videoFile: null,
    previewUrl: '',
    customPreviewFile: null,
    customPreviewUrl: '',
    customPreviewSize: 'contain',
    placeholder: 'https://youtube.com',
    isShorts: false,
    playButton: createPlayButtonDefaults(),
    position: createExtendedPositionLegacy({
        width: 556,
        tabletWidth: 556,
        mobileWidth: 320,
        height: 312,
        tabletHeight: 312,
        mobileHeight: 180,
    }),
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
    animation: createAnimationLegacy(),
    isSynchronizeStyle: false,
});

/**
 * Factory for the current legacy video entity — matches the persisted widget
 * JSON shape: `{ id, name: 'video', columnId, data, … }`. Variant selects
 * between default-column placement (no `type` field, simplified layout
 * shorthand) and zero-block placement (`type: 'zero'`, extended-position
 * grid + hide/animation/isSynchronizeStyle).
 *
 * When the redesigned canonical `default/v0` lands, add a sibling
 * `createDefaultVideo` returning that shape; the registry will dispatch
 * between them on a discriminator field.
 */
export function createLegacyVideo(variant?: 'default'): LegacyVideoDefault;
export function createLegacyVideo(variant: 'zero'): LegacyVideoZero;
export function createLegacyVideo(variant: LegacyVideoVariant = 'default'): LegacyVideo {
    if (variant === 'zero') {
        return {
            id: '',
            name: 'video',
            columnId: '',
            type: 'zero',
            data: createZeroData(),
        };
    }

    return {
        id: '',
        name: 'video',
        columnId: '',
        data: createDefaultData(),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default
 * for new video widgets. While only `legacy` exists, this delegates to
 * {@link createLegacyVideo}; pass the variant to opt into the zero-block tag.
 */
export function createVideo(variant?: 'default'): LegacyVideoDefault;
export function createVideo(variant: 'zero'): LegacyVideoZero;
export function createVideo(variant?: LegacyVideoVariant): VideoEntity {
    // `as any` because the overload signatures resolve at the call site; the
    // implementation just forwards the variant.
    return createLegacyVideo(variant as any);
}
